import { ENTITY_ID } from '../../domain/domainCodes.js'
import { ApiError, api } from '../../lib/api'
import { readDataTable } from '../../lib/dataTable'
import { isEnglish } from '../../lib/i18n'

/**
 * التلعيب endpoints. All six slices reach the API — nothing here reads
 * `src/data/gamification.js` any more.
 */




/**
 * التلعيب — the three configuration slices, all `GamificationManagers`.
 *
 *   getPointsSettings   -> GET  /admin/points-activities/     settings + the 14 event types
 *   savePointsSettings  -> POST /admin/points-activities/save-settings
 *   listActivities      -> POST /admin/points-activities/filter
 *   saveActivity        -> POST /admin/points-activities/save
 *   getBadgesPage       -> GET  /admin/points-badges/
 *   setBadgesAwarding   -> POST /admin/points-badges/set-badges-awarding
 *   listBadges          -> POST /admin/points-badges/filter
 *   saveBadge           -> POST /admin/points-badges/save
 *   listRiskLevels      -> POST /admin/pointsrisklevels/filter
 *   saveRiskLevel       -> POST /admin/pointsrisklevels/save
 *
 * THESE ARE NOT `/api` ROUTES. None of the six gamification slices has an `apiGroup` — every
 * action sits on the same `/admin/...` prefix as the page it belonged to, and those are the
 * live URLs, so they are called where they are (R1/R3) rather than duplicated under `/api`.
 * The prefixes are in the dev proxy WITH a trailing slash, so the bare page URL still reaches
 * the SPA and its legacy redirect.
 *
 * BOOLEANS MUST BE THE LITERAL STRING "true". Every one of these endpoints tests
 * `form["X"].ToString().Equals("true", OrdinalIgnoreCase)`, so `"on"`, `"1"` and `"yes"` all
 * bind FALSE — the value an HTML checkbox posts by default would silently turn a flag off.
 *
 * SUCCESS AND FAILURE COME BACK IN DIFFERENT CASINGS. The happy path returns a typed DTO
 * (`{Result, Message}`, PascalCase); the exception path returns a hand-built anonymous object
 * (`{result, message}`, camelCase). Both are HTTP 200. `outcome()` below reads either.
 */
/* The ACTIONS keep their `/admin/...` prefix — those are the live URLs and this slice never
   had an apiGroup. Only the page PAYLOADS moved to `/api/admin/...`, because a JSON route on
   `/admin/points-activities` itself would shadow the legacy bookmark and answer 401 to a
   browser instead of letting the SPA load. */
const ACTIVITIES = '/admin/points-activities'
const BADGES = '/admin/points-badges'
const RISK_LEVELS = '/admin/pointsrisklevels'

const ACTIVITIES_PAGE = '/api/admin/points-activities'
const BADGES_PAGE = '/api/admin/points-badges'

const label = (arabic, english) => (isEnglish ? english : arabic) || arabic || english || ''

/** `true` only for the literal string the server accepts. */
const asBool = (value) => value === true || value === 'true' || value === 1 || value === '1'

/** Reads both casings — see the note above. */
const outcome = (data) => ({
  ok: (data?.Result ?? data?.result) !== false,
  message: data?.Message ?? data?.message ?? '',
})

async function post(url, fields) {
  const body = new URLSearchParams()
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined && value !== null) body.set(key, String(value))
  }

  try {
    return outcome(await api.post(url, body))
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/**
 * A DataTables list. `length` is sent explicitly everywhere — the activities handler happens
 * to guard a missing one into 10 rows, but its two siblings do not, and relying on which is
 * which per slice is how a list silently comes back empty.
 */
async function list(url, { page = 1, pageSize = 25, query = '' } = {}, signal) {
  const params = new URLSearchParams({
    draw: '1',
    start: String((page - 1) * pageSize),
    // Clamped to 100 server-side; asking for more silently gets 100.
    length: String(Math.min(pageSize, 100)),
    'search[value]': query ?? '',
  })

  const dto = await api.post(url, params, { signal })
  if (dto?.Error) throw new ApiError(dto.Error)
  return readDataTable(dto)
}

/* ------------------------------------------------------------------ *
 * Activities — what earns or costs points, and the settings around them.
 * ------------------------------------------------------------------ */

/**
 * The settings block, and the fourteen event types an activity can be about.
 *
 * The event types exist ONLY here: `/filter` returns an activity's `EventType` number and a
 * pre-localised `EventTypeName`, but never the list of what a new one could be.
 */
export async function getPointsSettings(signal) {
  const dto = (await api.get(`${ACTIVITIES_PAGE}/`, { signal })) ?? {}

  return {
    enabled: Boolean(dto.GamificationEnabled),
    notifications: Boolean(dto.ActivateGamificationNotifications),
    showLeaderBoard: Boolean(dto.ShowLeaderBoard),
    topUsersOnlyWithBadges: Boolean(dto.GamificationTopUsersOnlyWithBadges),
    leaderBoardNameAr: dto.LeaderBoardScreenNameAr ?? '',
    leaderBoardNameEn: dto.LeaderBoardScreenNameEn ?? '',
    leaderBoardUsersCount: dto.LeaderBoardUsersCount ?? 10,
    eventTypes: (dto.EventTypes ?? []).map((row) => ({
      value: row.Value,
      label: row.Label ?? '',
    })),
  }
}

/**
 * All seven settings save together — this is one endpoint, not seven.
 *
 * The field names are inconsistently cased in the handler (`isEnabled` beside
 * `ActivateGamificationNotifications`); form lookups are case-insensitive so it does not
 * matter, but they are written as the handler reads them.
 */
export const savePointsSettings = (values) =>
  post(`${ACTIVITIES}/save-settings`, {
    isEnabled: asBool(values.enabled),
    ActivateGamificationNotifications: asBool(values.notifications),
    showLeaderBoard: asBool(values.showLeaderBoard),
    gamificationTopUsersOnlyWithBadges: asBool(values.topUsersOnlyWithBadges),
    leaderBoardScreenNameAr: values.leaderBoardNameAr ?? '',
    leaderBoardScreenNameEn: values.leaderBoardNameEn ?? '',
    leaderBoardUsersCount: Number(values.leaderBoardUsersCount) || 0,
  })

export async function listActivities(options, signal) {
  const { rows, total } = await list(`${ACTIVITIES}/filter`, options, signal)
  return {
    rows: rows.map((dto) => ({
      id: dto.Id,
      eventType: dto.EventType,
      eventTypeName: dto.EventTypeName ?? '',
      points: dto.Points ?? 0,
      isPositive: Boolean(dto.IsPositive),
      /* `EffectOpposit` — the API's spelling, kept at the boundary and corrected inward. It
         means the activity also REVERSES an earlier opposite award. */
      effectOpposite: Boolean(dto.EffectOpposit),
      percentageOnDuplication: dto.PercentageOnDuplication ?? null,
      isActive: Boolean(dto.IsActive),
    })),
    total,
  }
}

/** `Id` of -1 creates — and so does a missing or unparseable one. */
export const saveActivity = (values) =>
  post(`${ACTIVITIES}/save`, {
    Id: Number(values.id) > 0 ? Number(values.id) : ENTITY_ID.NEW,
    EventType: Number(values.eventType) || 0,
    Points: Number(values.points) || 0,
    IsPositive: asBool(values.isPositive),
    EffectOpposit: asBool(values.effectOpposite),
    PercentageOnDuplication: values.percentageOnDuplication === '' ? '' : Number(values.percentageOnDuplication) || 0,
    IsActive: asBool(values.isActive),
  })

/* ------------------------------------------------------------------ *
 * Badges — what a user collects, and how they are handed out.
 * ------------------------------------------------------------------ */

export async function getBadgesPage(signal) {
  const dto = (await api.get(`${BADGES_PAGE}/`, { signal })) ?? {}

  return {
    /* "all" keeps every badge a user has earned; "last" shows only the most recent. Two
       literal lowercase values, and the server defaults to "all". */
    awarding: dto.BadgesAwarding ?? 'all',
    directory: dto.BadgesDirectory ?? 'BadgesDirectory',
  }
}

export const setBadgesAwarding = (awarding) =>
  post(`${BADGES}/set-badges-awarding`, { BadgesAwarding: awarding })

export async function listBadges(options, signal) {
  const { rows, total } = await list(`${BADGES}/filter`, options, signal)
  return {
    rows: rows.map((dto) => ({
      id: dto.Id,
      name: label(dto.NameAr, dto.NameEn),
      nameAr: dto.NameAr ?? '',
      nameEn: dto.NameEn ?? '',
      descriptionAr: dto.DescriptionAr ?? '',
      descriptionEn: dto.DescriptionEn ?? '',
      points: dto.Points ?? 0,
      isActive: Boolean(dto.IsActive),
    })),
    total,
  }
}

/** The image is a FULL data URL (`data:image/png;base64,…`), or omitted to keep the current one. */
export const saveBadge = (values) =>
  post(`${BADGES}/save`, {
    Id: Number(values.id) > 0 ? Number(values.id) : ENTITY_ID.NEW,
    NameAr: values.nameAr ?? '',
    NameEn: values.nameEn ?? '',
    Points: Number(values.points) || 0,
    DescriptionAr: values.descriptionAr ?? '',
    DescriptionEn: values.descriptionEn ?? '',
    IsActive: asBool(values.isActive),
    Image: values.image ?? '',
  })

/**
 * Where a badge's picture is served from.
 *
 * NOT under `/root`, unlike every other stored file in this product. `SavePointsBadgeHandler`
 * writes to `Path.Combine(env.WebRootPath, "BadgesDirectory")` — that is `wwwroot/` itself,
 * beside `assets` and `app`, not `wwwroot/root/` where the media convention puts uploads. The
 * directory does not exist until the first badge image is saved, so a missing picture is the
 * normal state and the cell hides it rather than showing a broken image.
 */
export const badgeImageUrl = (directory, id) =>
  id ? `/${directory || 'BadgesDirectory'}/${Number(id)}.png` : null

/* ------------------------------------------------------------------ *
 * Risk levels — the bands a user's score falls into.
 *
 * THERE IS NO PAGE READER HERE. `GET /api/admin/pointsrisklevels/` answers `{ IsArabic }`
 * and nothing else — the culture the SPA already knows from its own locale — so the reader
 * that wrapped it returned a value no caller could want. The levels themselves come from
 * `listRiskLevels`, which the analysis screen calls.
 * ------------------------------------------------------------------ */

export async function listRiskLevels(options, signal) {
  const { rows, total } = await list(`${RISK_LEVELS}/filter`, options, signal)
  return {
    rows: rows.map((dto) => ({
      id: dto.Id,
      name: label(dto.NameAr, dto.NameEn),
      nameAr: dto.NameAr ?? '',
      nameEn: dto.NameEn ?? '',
      min: dto.Min ?? 0,
      /* Null is not "zero" — it is an OPEN-ENDED top band. Sent back empty to mean the same. */
      max: dto.Max ?? null,
      isActive: Boolean(dto.IsActive),
    })),
    total,
  }
}

export const saveRiskLevel = (values) =>
  post(`${RISK_LEVELS}/save`, {
    Id: Number(values.id) > 0 ? Number(values.id) : ENTITY_ID.NEW,
    NameAr: values.nameAr ?? '',
    NameEn: values.nameEn ?? '',
    Min: Number(values.min) || 0,
    // Empty means no ceiling; the handler parses a blank into null.
    Max: values.max === '' || values.max === null || values.max === undefined ? '' : Number(values.max),
    IsActive: asBool(values.isActive),
  })

/* ------------------------------------------------------------------ *
 * Points analysis, resets and transactions — the reporting half of التلعيب.
 *
 *   getAnalysisPage   -> GET  /api/admin/points-analysis/         org report + per-band chart
 *   getAnalysisReport -> POST /admin/points-analysis/get-report   the same, for a date range
 *   listAnalysis      -> POST /admin/points-analysis/filter       one row per user
 *   listResetLogs     -> POST /admin/pointsresetlogs/filter
 *   createReset       -> POST /admin/pointsresetlogs/save         { Notes } — RESETS EVERYONE
 *   getTransactionsPage -> GET /api/admin/pointstransactions/?resetLogId=
 *   listTransactions  -> POST /admin/pointstransactions/filter
 *
 * THE DATE TRAP, and here it is worse than dropping a filter. Every date on these three is
 * read with a bare `DateTime.TryParse` and no culture, under a default culture of ar-SA whose
 * calendar is UmAlQura — where `2026-08-09` and `2026-08-09 00:00` both FAIL. The failure is
 * silent: the range becomes null.
 *
 * On ANALYSIS that does not merely widen the query, it CHANGES it. With no range the handler
 * reads all-time totals off `Profiles`; with one it rolls up `PointsTransactions` for the
 * period. A reader who picks a month and gets all-time numbers has no way to tell. So every
 * date leaves here as `yyyy-MM-ddTHH:mm:ss`.
 *
 * The legacy screens send `YYYY-MM-DD`, so their date filters are dead in Arabic and work in
 * English. API_FINDINGS.md §60.
 */
const ANALYSIS = '/admin/points-analysis'
const RESET_LOGS = '/admin/pointsresetlogs'
const TRANSACTIONS = '/admin/pointstransactions'

const ANALYSIS_PAGE = '/api/admin/points-analysis'
const TRANSACTIONS_PAGE = '/api/admin/pointstransactions'

/** `yyyy-MM-ddTHH:mm:ss`, local, no zone — the only form that parses. See above. */
function toApiDate(value, endOfDay = false) {
  if (!value) return ''
  const date = String(value).slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return ''
  return `${date}T${endOfDay ? '23:59:59' : '00:00:00'}`
}

/**
 * `Username` ARRIVES AS AN ANCHOR, not a name.
 *
 * The reset-log and transaction projections build
 * `"<a href='{DomainURL}/admin/userdetailsadmin?Id={guid}'>{username}</a>"` server-side — and
 * `DomainURL` is a setting that may be empty, so the href is often broken anyway. The text is
 * taken and the markup dropped; where a link is wanted the screen builds its own from the id
 * the row already carries.
 *
 * The ANALYSIS list is the exception — its `Username` is a bare name. This returns a value it
 * finds no anchor in unchanged, so the same mapper covers all three.
 */
function textFromAnchor(value) {
  if (!value) return ''
  const match = /<a[^>]*>([\s\S]*?)<\/a>/i.exec(value)
  return (match ? match[1] : value).replace(/<[^>]*>/g, '').trim()
}

/** The guid inside that anchor, when there is one — the only place a transaction's user id is. */
function idFromAnchor(value) {
  const match = /[?&]Id=([0-9a-f-]{36})/i.exec(String(value ?? ''))
  return match ? match[1] : null
}

async function listPoints(url, { page = 1, pageSize = 25, query = '' } = {}, extra = {}, signal) {
  const params = new URLSearchParams({
    draw: '1',
    start: String((page - 1) * pageSize),
    // Required on all three: a missing length binds 0 and the handler takes no rows.
    length: String(Math.min(pageSize, 100)),
    'search[value]': query ?? '',
  })
  for (const [key, value] of Object.entries(extra)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  }

  const dto = await api.post(url, params, { signal })
  if (dto?.Error) throw new ApiError(dto.Error)
  return readDataTable(dto)
}

/* ------------------------------------------------------------------ *
 * Analysis
 * ------------------------------------------------------------------ */

const toOrganisationReport = (dto) => ({
  positive: dto?.TotalPositivePoints ?? 0,
  negative: dto?.TotalNegativePoints ?? 0,
  averageNegative: dto?.AverageNegativePoints ?? 0,
  users: dto?.TotalUsersCount ?? 0,
  riskLevelId: dto?.OrganizationRiskLevelId ?? null,
  riskLevel: dto?.OrganizationRiskLevel ?? '',
})

/** The organisation's standing, and how its people spread across the risk bands. */
export async function getAnalysisPage(signal) {
  const dto = (await api.get(`${ANALYSIS_PAGE}/`, { signal })) ?? {}

  return {
    report: toOrganisationReport(dto.OrganizationReport),
    /* One entry per risk level, with how many people sit in it. Carries its own id, so the
       screen colours a band by identity rather than by matching its NAME — the previous
       screen keyed its colours on the Arabic labels and lost them all in English. */
    bands: (dto.OrganizationChart ?? []).map((row) => ({
      id: row.RiskLevelId,
      name: row.RiskLevelName ?? '',
      min: row.RiskLevelMin ?? 0,
      users: row.RiskLevelUsersCount ?? 0,
      points: row.RiskLevelPointsCount ?? 0,
    })),
    badgesDirectory: dto.BadgesDirectory ?? 'BadgesDirectory',
  }
}

/** The same figures for a date range. Empty dates mean all time — see the note on dates. */
export async function getAnalysisReport({ from = '', to = '' } = {}, signal) {
  const body = new URLSearchParams()
  if (from) body.set('from', toApiDate(from))
  if (to) body.set('to', toApiDate(to, true))

  const dto = await api.post(`${ANALYSIS}/get-report`, body, { signal })
  return toOrganisationReport(dto?.Data)
}

/**
 * One row per user.
 *
 * `isPositive` IS NOT OPTIONAL HERE even though it looks it: the query binds a NON-nullable
 * bool, so leaving it out means `false`, which is an active filter for NEGATIVE points rather
 * than "no filter". There is no way to ask for both, so the screen makes it an explicit
 * choice and this always sends one.
 */
export async function listAnalysis(
  { page, pageSize, query, from = '', to = '', isPositive = false } = {},
  signal,
) {
  const { rows, total } = await listPoints(
    `${ANALYSIS}/filter`,
    { page, pageSize, query },
    {
      from: from ? toApiDate(from) : '',
      to: to ? toApiDate(to, true) : '',
      isPositive: isPositive ? 'true' : 'false',
    },
    signal,
  )

  return {
    rows: rows.map((dto) => ({
      id: dto.Id,
      username: textFromAnchor(dto.Username),
      name: dto.Name ?? '',
      department: dto.Department ?? '',
      negative: dto.PointsNegativeTotal ?? 0,
      positive: dto.PointsPositiveTotal ?? 0,
      riskLevelId: dto.PointsRiskLevelId ?? null,
      riskLevel: dto.PointsRiskLevel ?? '',
      /* Already localised server-side and flattened to one `Name` — the only badge projection
         in the feature that does NOT send the Ar/En pair. */
      badges: (dto.Badges ?? []).map((badge) => ({
        id: badge.Id,
        name: badge.Name ?? '',
        points: badge.Points ?? 0,
      })),
    })),
    total,
  }
}

/* ------------------------------------------------------------------ *
 * Resets, and the transactions they scope
 * ------------------------------------------------------------------ */

export async function listResetLogs(options, signal) {
  const { rows, total } = await listPoints(`${RESET_LOGS}/filter`, options, {}, signal)
  return {
    rows: rows.map((dto) => ({
      id: dto.Id,
      /* The anchor's text is the name; `CreatedBy` carries the same person's id cleanly, so
         the screen links from that rather than from markup the server built. */
      createdBy: textFromAnchor(dto.Username),
      createdById: dto.CreatedBy,
      // Pre-formatted `dd-MM-yyyy hh:mm tt` in en-US, and "" for a null or pre-1900 date.
      createdOn: dto.CreatedOn ?? '',
      notes: dto.Notes ?? '',
    })),
    total,
  }
}

/**
 * CREATES A RESET — this zeroes every user's points and starts a new period.
 *
 * The only field read is `Notes`; the id and the actor are the server's own (`CreatedBy` comes
 * from the signed-in user's claim, never from the client). There is no undo.
 */
export const createReset = (notes) => post(`${RESET_LOGS}/save`, { Notes: notes ?? '' })

/** Which reset a transaction list is scoped to, if any. */
export async function getTransactionsPage(resetLogId, signal) {
  const suffix = resetLogId ? `?resetLogId=${Number(resetLogId)}` : ''
  const dto = (await api.get(`${TRANSACTIONS_PAGE}/${suffix}`, { signal })) ?? {}
  const log = dto.ResetLog

  return log
    ? {
        id: log.Id,
        createdBy: textFromAnchor(log.Username),
        createdOn: log.CreatedOn ?? '',
        notes: log.Notes ?? '',
      }
    : null
}

/**
 * The ledger. 5,800 rows and growing, so it is always server-paged.
 *
 * Six filters, all read from the FORM only (never the query) and all lower-camel:
 * `from`, `to`, `userId`, `isPositive`, `pointsActivityId`, `pointsResetLogId`. Unlike the
 * analysis filter, `isPositive` here is a proper `bool?` — omitting it really does mean both.
 */
export async function listTransactions(
  { page, pageSize, query, from = '', to = '', userId = '', isPositive = null, activityId = '', resetLogId = '' } = {},
  signal,
) {
  const { rows, total } = await listPoints(
    `${TRANSACTIONS}/filter`,
    { page, pageSize, query },
    {
      from: from ? toApiDate(from) : '',
      to: to ? toApiDate(to, true) : '',
      userId,
      // Only sent when it is a real choice — absent means both, which is what we want.
      isPositive: isPositive === null ? '' : String(Boolean(isPositive)),
      pointsActivityId: activityId,
      pointsResetLogId: resetLogId,
    },
    signal,
  )

  return {
    rows: rows.map((dto) => ({
      id: dto.Id,
      userId: dto.UserId,
      username: textFromAnchor(dto.Username),
      userLinkId: idFromAnchor(dto.Username) ?? dto.UserId,
      timestamp: dto.Timestamp ?? '',
      activity: label(dto.PointsActivityAr, dto.PointsActivityEn),
      activityId: dto.PointsActivityId,
      points: dto.Points ?? 0,
      isPositive: Boolean(dto.IsPositive),
      effectOpposite: Boolean(dto.EffectOpposit),
      percentageOnDuplication: dto.PercentageOnDuplication ?? null,
      resetLogId: dto.PointsResetLogId ?? null,
      resetLogDate: dto.PointsResetLogDate ?? '',
    })),
    total,
  }
}
